'use client';

import { InfoCircle } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { IconWithTooltip } from '../filters/components/IconWithTooltip/IconWithTooltip';
import type { Progress } from './types';

type ProgressBarProps = {
  progress: Progress;
  labelLeft?: string | React.ReactNode;
  labelRight?: string | React.ReactNode;
  showTooltip?: boolean;
  trackColour?: 'bg-container' | 'bg-container-secondary';
};

/**
 * Renders a progress bar with % filled, and option left and right labels.
 * Utility getProgressLabel can help format common labels e.g. '£10 of £50'.
 */
function ProgressBar({ progress, labelLeft, labelRight, showTooltip = false, trackColour = 'bg-container' }: ProgressBarProps) {
  const [currentProgress, setCurrentProgress] = useState(0);
  const t = useTranslations('offers.page');

  // Dev error to defend against divide by 0 and other erroneous states.
  if (progress.max === 0) throw new Error('progress.max cannot be 0');

  const classes = {
    wrapper: `mt-2`,
    labelWrapper: `flex justify-between items-center`,
    label: `flex items-center gap-1 text-foreground paragraph-s-strong mb-2 mt-1`,
    container: `w-full h-1 md:h-2 rounded overflow-hidden ${trackColour}`,
    fill: `h-full bg-accent-green transition-all duration-300 ease-in-out border border-accent-green rounded-sm`,
  };

  const progressPercentage = (100 / progress.max) * progress.current;

  // Animate progress fill on initial render (zero to current progress).
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const animateProgress = async () => {
      await new Promise(resolve => {
        timeoutId = setTimeout(resolve, 100);
      });
      setCurrentProgress(progressPercentage);
    };

    animateProgress();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [progressPercentage]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.labelWrapper}>
        <p className={classes.label}>
          {labelLeft}
          {showTooltip && (
            <IconWithTooltip tooltipContent={t('wageringRequirementsTooltip')} ariaLabel={t('wagering_requirements_aria_label')}>
              <InfoCircle size="extra-small" />
            </IconWithTooltip>
          )}
        </p>
        <span className={classes.label}>{labelRight}</span>
      </div>
      <div className={classes.container}>
        <div className={classes.fill} style={{ width: `${currentProgress}%` }} data-testid="progress-fill" />
      </div>
    </div>
  );
}

export { ProgressBar };
